import 'url-polyfill';
import { Callable, Constructable, FunctionClone } from './types';
import { PW_QUERY_KEY } from '@ringcentral/rwc-utils/src/constants/misc';

export const getPrivateMeetingPassQuery = meeting => {
    const { description } = meeting;

    const urlRegExp = /https?:\/\/.+?\?pw=.+?\b/m;

    const url = description.match(urlRegExp);

    const passwordParam = url
        ? `?${PW_QUERY_KEY}=${new URL(url[0]).searchParams.get(PW_QUERY_KEY)}`
        : '';

    return passwordParam;
};

export const noop: <T>(prop?: T) => void = prop => void 0;

// Checks object for undefined props.
export function hasUndefinedProps(obj: Record<string, unknown>): boolean {
    return Object.values(obj).some(item => item === undefined);
}

export const isWebinar = location.pathname.startsWith('/w/');

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const isHercules = (publicPath = __webpack_public_path__): boolean =>
    Boolean(publicPath) && // eslint-disable no-undef, camelcase
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    !['/', 'auto', '/static/'].includes(publicPath); // eslint-disable no-undef, camelcase

export const isOkResponse = ({ status }: any): boolean =>
    !status || status.toString().startsWith('2');

export function generateUid(): string {
    return Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map(v => v.toString(16).padStart(2, '0'))
        .join('');
}

export const generateCBIframeId = prop => `cb-${prop}-${generateUid()}`;

export const isObject = (value: unknown): boolean => typeof value === 'object' && value !== null;

export const extractBool = (val: unknown): boolean | undefined => {
    if (val === 'true') return true;
    return val === 'false' ? false : undefined;
};

// The "Unicode Problem" https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_unicode_problem
// btoa() only support characters from String.fromCodePoint(0) up to String.fromCodePoint(255).
// also check https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings
const RADIX = 16;
const PERCENT_ENCODED_START_SIGN = '%';
const PERCENT_ENCODED_UTF8_REGEXP = new RegExp(`${PERCENT_ENCODED_START_SIGN}([0-9A-F]{2})`, 'g');
const PERCENT_ENCODED_START_CODE_WITHOUT_SIGN = '00';

// Encoding UTF8 ⇢ base64
/*
    first we use encodeURIComponent to get percent-encoded UTF-8,
    then we convert the percent encodings into raw bytes which
    can be fed into btoa.
*/
export const b64EncodeUnicode = (string: string): string =>
    btoa(
        encodeURIComponent(string).replace(PERCENT_ENCODED_UTF8_REGEXP, (_, clearEncodeUTF8) =>
            String.fromCharCode(parseInt(clearEncodeUTF8, RADIX))
        )
    );

// Decoding base64 ⇢ UTF8
// Going backwards: from bytestream, to percent-encoding, to original string.
export const b64DecodeUnicode = (string: string): string =>
    decodeURIComponent(
        atob(string)
            .split('')
            .map(
                char =>
                    PERCENT_ENCODED_START_SIGN +
                    (
                        PERCENT_ENCODED_START_CODE_WITHOUT_SIGN + char.charCodeAt(0).toString(RADIX)
                    ).slice(-2)
            )
            .join('')
    );

export function singleton<Type extends Constructable>(
    constr: Type
): (...args: ConstructorParameters<Type>) => Type {
    let instance;

    return (...args) => {
        if (!instance) {
            instance = new constr(...args);
        }

        return instance;
    };
}

export const getRandomInt = (max: number): number => Math.floor(Math.random() * Math.floor(max));

/** Returns previous result if the same arguments are given. */
export const memoizeLast = <T extends Callable>(f: T): FunctionClone<T> => {
    let prevArgs: Parameters<T> | [] = [];
    let prevResult;

    return (...args) => {
        if (args.length === prevArgs.length && args.every((arg, i) => arg === prevArgs[i])) {
            return prevResult;
        }

        prevArgs = args;
        prevResult = f(...args);
        return prevResult;
    };
};

/** Returns previous result if less than N ms passed since the last call. Doesn't depend on arguments. */
export const memoizeForNMs = <T extends Callable>(ms: number, f: T): FunctionClone<T> => {
    let lastCalculationTime = -Infinity;
    let lastResult: ReturnType<T>;

    return (...args) => {
        const now = Date.now();
        if (ms > 0 && now < lastCalculationTime + ms) return lastResult;

        lastCalculationTime = now;
        lastResult = f(...args);
        return lastResult;
    };
};

export const keyCodes = {
    SPACE: 32,
    ENTER: 13,
    ESC: 27,
    DOWN_ARROW: 40,
    UP_ARROW: 38,
    SHIFT: 16,
    TAB: 9,
};

export const keys = {
    TAB: 'Tab',
    ESC: 'Escape',
    RIGHT_ARROW: 'ArrowRight',
    LEFT_ARROW: 'ArrowLeft',
    ENTER: 'Enter',
    UP_ARROW: 'ArrowUp',
    DOWN_ARROW: 'ArrowDown',
    SPACE: 'Space',
};

export const getSecondsSince = (startTime: number): number =>
    Math.floor((Date.now() - startTime) / 1000) || 0;

/**
 * Transforms camel or snake cased string into kebab case.
 * @param {string} str
 * @returns {string} kebab cased string
 */
export const toKebabCase = (str: string) =>
    str
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replace(/[\s_]+/g, '-')
        .toLowerCase();

export const getAppVersion = version =>
    version.replace(/^(\d\d)(\d)/, '$1.$2').replace(/\.(\d)+$/, '');

/**
 * Get PMI or PMN from schedule meeting info.
 * @param str meeting description or meeting location from schedule meeting info.
 * @returns personal meeting name or personal meeting id extract from str
 */
export const matchMeetingIdLoosely = (str: string) => {
    if (!str) return null;
    const exp = /https?:\/(.+?)(\/join\/|\/conf\/on\/)(.+?)(\?|#|\/|\s|$)/gim;
    const expCheckResult = exp.exec(str);
    if (!expCheckResult) return null;
    return expCheckResult[3];
};
